import { Component, OnInit } from '@angular/core';
import { NgForOf, NgIf } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { IconComponent } from '../../../elements/icon/icon.component';
import { LoadingComponent } from '../../../elements/loading/loading.component';
import { Icons } from '../../../../model/icon';
import { AirportService } from '../../../../services/admin/airport.service';
import { ServiceService } from '../../../../services/admin/service.service';
import { BookingService } from '../../../../services/admin/booking.service';
import { LanguageService } from '../../../../services/admin/language.service';
import { CountryService } from '../../../../services/admin/country.service';
import { CityService } from '../../../../services/admin/city.service';
import { AirportServicesService } from '../../../../services/admin/airport-service.service';
import { TokenStorageService } from '../../../../services/token.storage.service';
import { MainScreenImageService } from '../../../../services/main-screen-image.service';

type ElementType = 'airports' | 'services' | 'bookings' | 'languages' | 'countries' | 'cities' | 'airport-services' | 'main-screen-images';

@Component({
  selector: 'app-admin-element-page',
  standalone: true,
  imports: [NgIf, NgForOf, FormsModule, ReactiveFormsModule, IconComponent, LoadingComponent],
  templateUrl: './admin-element-page.component.html',
  styleUrls: ['./admin-element-page.component.scss']
})
export class AdminElementPageComponent implements OnInit {
  elementType: ElementType = 'airports';
  elementId: string | null = null;
  isNew: boolean = false;

  loading: boolean = false;
  loadingForSaving: boolean = false;

  myForm: FormGroup;
  elementData: any = {};

  bookingPassengersDetails: any[] = [];
  bookingAirports: any[] = [];

  airportServicesList: any[] = [];

  options: any[] = [];

  protected readonly Icons = Icons;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private airportService: AirportService,
    private serviceService: ServiceService,
    private bookingService: BookingService,
    private languageService: LanguageService,
    private countryService: CountryService,
    private cityService: CityService,
    private tokenStorageService: TokenStorageService,
    private airportServicesService: AirportServicesService,
    private mainScreenImageService: MainScreenImageService
  ) {
    this.myForm = this.fb.group({});
  }

  ngOnInit() {
    if (!this.tokenStorageService.isAuthenticated()) {
      this.router.navigate(['/login']);
      return;
    }

    this.route.params.subscribe(params => {
      this.elementType = params['type'] || 'airports';
      this.elementId = params['id'] || null;
      this.isNew = this.elementId === 'new';

      this.initForm();

      if (this.isNew) {
        this.elementData = this.getEmptyData();
        this.myForm.patchValue(this.mapDataToForm(this.elementData));
        this.loading = false;
      } else {
        this.loadData();
      }
    });
  }

  initForm() {
    this.myForm.reset();
    Object.keys(this.myForm.controls).forEach(control => {
      this.myForm.removeControl(control);
    });

    switch (this.elementType) {
      case 'airports':
        this.myForm.addControl('id', this.fb.control({ value: '', disabled: true }));
        this.myForm.addControl('name', this.fb.control('', Validators.required));
        this.myForm.addControl('iata_code', this.fb.control(''));
        this.myForm.addControl('icao_code', this.fb.control(''));
        this.myForm.addControl('city_code', this.fb.control(''));
        this.myForm.addControl('country_code', this.fb.control(''));
        this.myForm.addControl('timezone', this.fb.control(''));
        this.myForm.addControl('website', this.fb.control(''));
        this.myForm.addControl('phone', this.fb.control(''));
        this.myForm.addControl('email', this.fb.control(''));
        break;

      case 'services':
        this.myForm.addControl('id', this.fb.control({ value: '', disabled: true }));
        this.myForm.addControl('name', this.fb.control('', Validators.required));
        this.myForm.addControl('description', this.fb.control(''));
        break;

      case 'bookings':
        this.myForm.addControl('id', this.fb.control({ value: '', disabled: true }));
        this.myForm.addControl('status', this.fb.control('DRAFT', Validators.required));
        this.myForm.addControl('tripType', this.fb.control('one_way'));
        this.myForm.addControl('subtotal', this.fb.control({ value: 0, disabled: true }));
        this.myForm.addControl('total', this.fb.control({ value: 0, disabled: true }));
        this.myForm.addControl('passengersCount.adult', this.fb.control({ value: 0, disabled: true }));
        this.myForm.addControl('passengersCount.children', this.fb.control({ value: 0, disabled: true }));
        this.myForm.addControl('passengersCount.infant', this.fb.control({ value: 0, disabled: true }));
        this.myForm.addControl('flight.flightNumber', this.fb.control('', Validators.required));
        this.myForm.addControl('flight.date', this.fb.control('', Validators.required));
        this.myForm.addControl('flight.time', this.fb.control(''));
        this.myForm.addControl('flight.departure', this.fb.control(''));
        this.myForm.addControl('flight.arrival', this.fb.control(''));
        this.myForm.addControl('flight.duration', this.fb.control(''));
        this.myForm.addControl('flight.terminal', this.fb.control(''));
        this.myForm.addControl('flight.direction', this.fb.control(''));
        this.myForm.addControl('flight.flightType', this.fb.control(''));
        this.myForm.addControl('contact.phone', this.fb.control(''));
        this.myForm.addControl('contact.email', this.fb.control('', Validators.email));
        break;

      case 'languages':
        this.myForm.addControl('code639_1', this.fb.control({ value: '', disabled: !this.isNew }, Validators.required));
        this.myForm.addControl('code639_3', this.fb.control(''));
        this.myForm.addControl('englishName', this.fb.control(''));
        this.myForm.addControl('nativeName', this.fb.control(''));
        break;

      case 'countries':
        this.myForm.addControl('code', this.fb.control({ value: '', disabled: !this.isNew }, Validators.required));
        this.myForm.addControl('code3', this.fb.control(''));
        this.myForm.addControl('name', this.fb.control(''));
        this.myForm.addControl('continent', this.fb.control(''));
        this.myForm.addControl('population', this.fb.control(0));
        this.myForm.addControl('currency', this.fb.control(''));
        break;

      case 'cities':
        this.myForm.addControl('slug', this.fb.control({ value: '', disabled: !this.isNew }, Validators.required));
        this.myForm.addControl('city_code', this.fb.control(''));
        this.myForm.addControl('name', this.fb.control(''));
        this.myForm.addControl('country_code', this.fb.control(''));
        this.myForm.addControl('timezone', this.fb.control(''));
        this.myForm.addControl('population', this.fb.control(0));
        break;

      case 'airport-services':
        this.myForm.addControl('airportId', this.fb.control({ value: '', disabled: !this.isNew }, Validators.required));
        this.myForm.addControl('information', this.fb.control(''));
        break;

      case 'main-screen-images':
        this.myForm.addControl('id', this.fb.control({ value: '', disabled: true }));
        this.myForm.addControl('url', this.fb.control('', Validators.required));
        break;
    }
  }

  loadData() {
    this.loading = true;

    if (this.elementType === 'airport-services') {
      const id = Number(this.elementId);
      if (!isNaN(id)) {
        this.airportServicesService.getByAirportId(id).subscribe({
          next: (data: any) => {
            this.elementData = data;
            this.airportServicesList = data?.services || [];
            this.myForm.patchValue({ airportId: data?.airportId ?? id });
            this.loading = false;
          },
          error: () => {
            this.loading = false;
          }
        });
      } else {
        this.loading = false;
      }
      return;
    }

    if (this.elementType === 'main-screen-images') {
      this.mainScreenImageService.getList().subscribe({
        next: (data: any[]) => {
          const image = data.find(img => img.id === Number(this.elementId));
          if (image) {
            this.elementData = image;
            this.myForm.patchValue(this.mapDataToForm(image));
          }
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        }
      });
      return;
    }

    if (this.elementType === 'airports') {
      if (!this.elementId) {
        this.elementData = this.getEmptyData();
        this.myForm.patchValue(this.mapDataToForm(this.elementData));
        this.loading = false;
        return;
      }

      this.airportService.getPublicAirportByCode(this.elementId).subscribe({
        next: (data: any) => {
          const airport = this.extractAirport(data);
          this.elementData = airport;
          this.myForm.patchValue(this.mapAirportToForm(airport));
          this.loading = false;
        },
        error: () => {
          this.elementData = this.getEmptyData();
          this.myForm.patchValue(this.mapDataToForm(this.elementData));
          this.loading = false;
        }
      });
      return;
    }

    const serviceMap: Record<Exclude<ElementType, 'airports' | 'airport-services' | 'main-screen-images'>, any> = {
      services: this.serviceService,
      bookings: this.bookingService,
      languages: this.languageService,
      countries: this.countryService,
      cities: this.cityService
    };

    const service = serviceMap[this.elementType as Exclude<ElementType, 'airports' | 'airport-services' | 'main-screen-images'>];
    if (!service) {
      this.loading = false;
      return;
    }

    service.getById(this.elementId).subscribe({
      next: (data: any) => {
        this.elementData = data;
        if (this.elementType === 'bookings') {
          this.bookingPassengersDetails = Array.isArray(data?.passengersDetails) ? data.passengersDetails : [];
          this.bookingAirports = Array.isArray(data?.airports) ? data.airports : [];
        }
        this.myForm.patchValue(this.mapDataToForm(data));
        this.loading = false;
      },
      error: () => {
        this.elementData = this.getEmptyData();
        this.myForm.patchValue(this.mapDataToForm(this.elementData));
        this.loading = false;
      }
    });
  }

  private extractAirport(data: any): any {
    return data?.data ?? data?.content ?? data?.airport ?? data;
  }

  private mapAirportToForm(data: any): any {
    return {
      id: data?.id ?? '',
      name: data?.name ?? '',
      iata_code: data?.iata_code ?? data?.iata ?? '',
      icao_code: data?.icao_code ?? data?.icao ?? '',
      city_code: data?.city_code ?? data?.cityCode ?? '',
      country_code: data?.country_code ?? data?.countryCode ?? '',
      timezone: data?.timezone ?? '',
      website: data?.website ?? '',
      phone: data?.phone ?? '',
      email: data?.email ?? ''
    };
  }

  private mapDataToForm(data: any): any {
    if (this.elementType !== 'bookings') {
      return data;
    }

    return {
      id: data?.id ?? '',
      status: data?.status ?? 'DRAFT',
      tripType: data?.tripType ?? 'one_way',
      subtotal: data?.subtotal ?? 0,
      total: data?.total ?? 0,
      'passengersCount.adult': data?.passengersCount?.adult ?? 0,
      'passengersCount.children': data?.passengersCount?.children ?? 0,
      'passengersCount.infant': data?.passengersCount?.infant ?? 0,
      'flight.flightNumber': data?.flight?.flightNumber ?? '',
      'flight.date': data?.flight?.date ?? '',
      'flight.time': data?.flight?.time ?? '',
      'flight.departure': data?.flight?.departure ?? '',
      'flight.arrival': data?.flight?.arrival ?? '',
      'flight.duration': data?.flight?.duration ?? '',
      'flight.terminal': data?.flight?.terminal ?? '',
      'flight.direction': data?.flight?.direction ?? '',
      'flight.flightType': data?.flight?.flightType ?? '',
      'contact.phone': data?.contact?.phone ?? '',
      'contact.email': data?.contact?.email ?? ''
    };
  }

  private mapFormToPayload(formValue: any): any {
    if (this.elementType !== 'bookings') {
      return formValue;
    }

    return {
      id: this.elementData?.id,
      airports: this.elementData?.airports || [],
      passengersDetails: this.elementData?.passengersDetails || [],
      status: formValue.status,
      tripType: formValue.tripType,
      subtotal: formValue.subtotal,
      total: formValue.total,
      flight: {
        flightNumber: formValue['flight.flightNumber'],
        date: formValue['flight.date'],
        time: formValue['flight.time'],
        departure: formValue['flight.departure'],
        arrival: formValue['flight.arrival'],
        duration: formValue['flight.duration'],
        terminal: formValue['flight.terminal'],
        direction: formValue['flight.direction'],
        flightType: formValue['flight.flightType']
      },
      passengersCount: {
        adult: formValue['passengersCount.adult'],
        children: formValue['passengersCount.children'],
        infant: formValue['passengersCount.infant']
      },
      contact: {
        phone: formValue['contact.phone'],
        email: formValue['contact.email']
      }
    };
  }

  private getEmptyData(): any {
    switch (this.elementType) {
      case 'airports':
        return { name: '', iata_code: '', icao_code: '', city_code: '', country_code: '', timezone: '', website: '', phone: '', email: '' };
      case 'services':
        return { name: '', description: '' };
      case 'bookings':
        return {
          status: 'DRAFT',
          tripType: 'one_way',
          subtotal: 0,
          total: 0,
          flight: { flightNumber: '', date: '', time: '', departure: '', arrival: '', duration: '', terminal: '', direction: '', flightType: '' },
          passengersCount: { adult: 0, children: 0, infant: 0 },
          passengersDetails: [],
          contact: { phone: '', email: '' },
          airports: []
        };
      case 'languages':
        return { code639_1: '', code639_3: '', englishName: '', nativeName: '' };
      case 'countries':
        return { code: '', code3: '', name: '', continent: '', population: 0, currency: '' };
      case 'cities':
        return { slug: '', city_code: '', name: '', country_code: '', timezone: '', population: 0 };
      case 'airport-services':
        return { airportId: '', services: [], information: '' };
      case 'main-screen-images':
        return { url: '' };
    }
  }

  onSubmit() {
    if (this.myForm.invalid) {
      alert('Пожалуйста, заполните все обязательные поля');
      return;
    }

    this.loadingForSaving = true;
    const formValue = this.myForm.getRawValue();

    let payload: any;
    if (this.elementType === 'bookings') {
      payload = this.mapFormToPayload(formValue);
    } else {
      payload = { ...formValue };
      Object.keys(payload).forEach(key => {
        if (this.myForm.get(key)?.disabled) {
          delete payload[key];
        }
      });

      if (!this.isNew) {
        payload = { ...this.elementData, ...payload };
      }
    }

    const serviceMap: Record<Exclude<ElementType, 'airport-services' | 'main-screen-images'>, any> = {
      airports: this.airportService,
      services: this.serviceService,
      bookings: this.bookingService,
      languages: this.languageService,
      countries: this.countryService,
      cities: this.cityService
    };

    let saveObservable;
    if (this.elementType === 'airport-services') {
      saveObservable = this.airportServicesService.save(payload);
    } else if (this.elementType === 'main-screen-images') {
      saveObservable = this.mainScreenImageService.save(payload);
    } else {
      const service = serviceMap[this.elementType as Exclude<ElementType, 'airport-services' | 'main-screen-images'>];
      saveObservable = service.save(payload);
    }

    saveObservable.subscribe({
      next: () => {
        this.loadingForSaving = false;
        alert('Сохранено успешно!');
        this.router.navigate(['/admin']);
      },
      error: () => {
        this.loadingForSaving = false;
        alert('Ошибка при сохранении');
      }
    });
  }

  canDelete(): boolean {
    return !this.isNew && this.elementType !== 'bookings' && this.elementType !== 'airport-services';
  }

  deleteItem() {
    if (!this.canDelete()) {
      return;
    }
    if (!confirm('Вы уверены, что хотите удалить этот элемент?')) {
      return;
    }

    this.loading = true;

    const serviceMap: Record<Exclude<ElementType, 'airport-services' | 'bookings' | 'main-screen-images'>, any> = {
      airports: this.airportService,
      services: this.serviceService,
      languages: this.languageService,
      countries: this.countryService,
      cities: this.cityService
    };

    let deleteObservable;
    if (this.elementType === 'main-screen-images') {
      deleteObservable = this.mainScreenImageService.delete(Number(this.elementId));
    } else {
      const service = serviceMap[this.elementType as Exclude<ElementType, 'airport-services' | 'bookings' | 'main-screen-images'>];
      deleteObservable = service.delete(this.elementId);
    }

    deleteObservable.subscribe({
      next: () => {
        this.loading = false;
        alert('Элемент удален');
        this.router.navigate(['/admin']);
      },
      error: () => {
        this.loading = false;
        alert('Ошибка при удалении');
      }
    });
  }

  back() {
    this.router.navigate(['/admin']).then(r => r);
  }

  openPage() {
    alert('Открыть страницу\n\nВ реальном приложении здесь будет переход на публичную страницу.');
  }

  getTitle(): string {
    const titles: Record<ElementType, string> = {
      airports: 'Airport',
      services: 'Service',
      bookings: 'Booking',
      languages: 'Language',
      countries: 'Country',
      cities: 'City',
      'airport-services': 'Airport Services',
      'main-screen-images': 'Main Screen Image'
    };
    return titles[this.elementType] || 'Item';
  }

  getElementLabel(): string {
    const d = this.elementData || {};
    return d.name || d.englishName || d.id || d.code || d.slug || d.url || '';
  }
}
